import React, { useEffect, useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Label,
  LabelList,
} from "recharts";
import { useAuth } from "../contexts/AuthContext";
import axiosInstance from "../axiosInstance";
import "./Dashboard.scss";
import LoadingScreen from "../components/LoadingScreen/LoadingScreen";

const COLORS = [
  "#0ea5e9",
  "#7c3aed",
  "#ef4444",
  "#f59e0b",
  "#10b981",
  "#6b7280",
  "#ec4899",
  "#8b5cf6",
];

// Compact Metric Card Component
const MetricCard = ({ title, value, color }) => (
  <div className="metric-card">
    <div className="metric-title">{title}</div>
    <div className="metric-value" style={{ color }}>
      {value}
    </div>
  </div>
);

// Mini Chart Card
const ChartCard = ({ title, children, height = "180px" }) => (
  <div className="chart-card">
    <h4 className="chart-title">{title}</h4>
    <div style={{ height }}>{children}</div>
  </div>
);

const ConicalFunnel = ({ data }) => {
  const stageHeight = 40;
  const maxWidth = 240;
  const minWidth = 40;
  const totalStages = data.length;
  const svgHeight = totalStages * stageHeight + 40;
  const svgWidth = 500;
  const centerX = svgWidth / 2;

  return (
    <div
      className="conical-funnel"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        transform: "scale(0.9)",
        transformOrigin: "center",
      }}
    >
      <svg width={svgWidth} height={svgHeight}>
        {data.map((stage, index) => {
          const y = index * stageHeight + 20;
          const topWidth =
            maxWidth - ((maxWidth - minWidth) * index) / totalStages;
          const bottomWidth =
            maxWidth - ((maxWidth - minWidth) * (index + 1)) / totalStages;

          const topLeft = centerX - topWidth / 2;
          const topRight = centerX + topWidth / 2;
          const bottomLeft = centerX - bottomWidth / 2;
          const bottomRight = centerX + bottomWidth / 2;

          const color = stage.color || `hsl(${(index * 50) % 360}, 70%, 60%)`;

          return (
            <g key={index}>
              <polygon
                points={`${topLeft},${y} ${topRight},${y} ${bottomRight},${
                  y + stageHeight
                } ${bottomLeft},${y + stageHeight}`}
                fill={color}
                stroke="#fff"
                strokeWidth="1"
              />
              <text
                x={topLeft - 50}
                y={y + stageHeight / 2}
                textAnchor="end"
                fontSize="11"
                fill="#222"
                alignmentBaseline="middle"
              >
                {stage.label}
              </text>
              <text
                x={topRight + 50}
                y={y + stageHeight / 2}
                textAnchor="start"
                fontSize="11"
                fill="#222"
                alignmentBaseline="middle"
              >
                {stage.count} ({stage.percentage}%)
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// Summary Stats Card
const SummaryStatsCard = ({ metrics }) => {
  const conversionRate = metrics.totalEnquiries
    ? ((metrics.approvedEnquiries / metrics.totalEnquiries) * 100).toFixed(1)
    : 0;

  const rejectionRate = metrics.totalEnquiries
    ? ((metrics.rejectedEnquiries / metrics.totalEnquiries) * 100).toFixed(1)
    : 0;

  return (
    <div className="chart-card summary-stats">
      <h4 className="chart-title">Conversion Insights</h4>
      <div className="summary-grid">
        <div className="summary-item">
          <div className="summary-label">Conversion Rate</div>
          <div className="summary-value" style={{ color: "#10b981" }}>
            {conversionRate}%
          </div>
        </div>
        <div className="summary-item">
          <div className="summary-label">Rejection Rate</div>
          <div className="summary-value" style={{ color: "#ef4444" }}>
            {rejectionRate}%
          </div>
        </div>
        <div className="summary-item">
          <div className="summary-label">Win Rate</div>
          <div className="summary-value" style={{ color: "#7c3aed" }}>
            {metrics.approvedEnquiries || 0}/{metrics.totalEnquiries || 0}
          </div>
        </div>
        <div className="summary-item">
          <div className="summary-label">Active Pipeline</div>
          <div className="summary-value" style={{ color: "#f59e0b" }}>
            {metrics.pendingEnquiries || 0}
          </div>
        </div>
      </div>
    </div>
  );
};

const renderPieLabel = (entry) => {
  return `${entry.name}: ${entry.value}`;
};

const Dashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    const stored = localStorage.getItem("dashboardViewMode");
    if (stored) return stored;
    // Default based on roles will be set in useEffect,
    // but set a safe fallback here
    return "CRM";
  });
  const [data, setData] = useState({
    leads: [],
    clients: [],
    enquiries: [],
    operations: [],
    products: [],
    enquiryStages: [],
  });

  const authorities = user?.authorities || [];
  const isSales = authorities.includes("ROLE_SALES");
  const isSalesManager = authorities.includes("ROLE_SALES-MANAGER");
  const isProdManager = authorities.includes("ROLE_PROD-MANAGER");
  const isProduction = authorities.includes("ROLE_OPERATIONS");
  const isAdmin = authorities.includes("ROLE_CRM-ADMIN");

  // Determine which views to show based on roles
  // CRM should be shown to: ROLE_SALES, ROLE_CRM-ADMIN
  const showCRM = isSales || isAdmin;

  // ERP should be shown to: ROLE_PROD-MANAGER, ROLE_SALES-MANAGER, ROLE_CRM-ADMIN, ROLE_OPERATIONS
  const showERP = isProdManager || isSalesManager || isAdmin;

  useEffect(() => {
    const stored = localStorage.getItem("dashboardViewMode");
    if (stored === "ERP" && !showERP) {
      setViewMode("CRM");
      localStorage.setItem("dashboardViewMode", "CRM");
    } else if (stored === "CRM" && !showCRM && showERP) {
      setViewMode("ERP");
      localStorage.setItem("dashboardViewMode", "ERP");
    } else if (stored) {
      setViewMode(stored);
    } else if (!showCRM && showERP) {
      // ✅ ADD THIS — handles first load with no localStorage
      setViewMode("ERP");
      localStorage.setItem("dashboardViewMode", "ERP");
    }
  }, [showCRM, showERP]);

  // Handle view mode toggle
  const handleViewModeToggle = (mode) => {
    setViewMode(mode);
    localStorage.setItem("dashboardViewMode", mode);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const promises = [];

        if (viewMode === "CRM" && showCRM) {
          const baseUrl = isSalesManager
            ? process.env.REACT_APP_SALES_MANAGER
            : process.env.REACT_APP_SALES;

          promises.push(
            axiosInstance
              .get(`${baseUrl}/leads/assigned-leads`)
              .then((res) => ({ type: "leads", data: res.data?.data || [] }))
              .catch(() => ({ type: "leads", data: [] })),

            axiosInstance
              .get(`${baseUrl}/clients/assigned`)
              .then((res) => ({ type: "clients", data: res.data?.data || [] }))
              .catch(() => ({ type: "clients", data: [] })),

            axiosInstance
              .get(`${baseUrl}/enquiry-stage-history/employee`)
              .then((res) => ({
                type: "enquiries",
                data: res.data?.data || [],
              }))
              .catch(() => ({ type: "enquiries", data: [] })),

            axiosInstance
              .get(`${baseUrl}/enquiry-stages/enabled`)
              .then((res) => ({
                type: "enquiryStages",
                data: res.data?.data || [],
              }))
              .catch(() => ({ type: "enquiryStages", data: [] })),
          );
        }

        if (viewMode === "ERP" && showERP) {
          const prodUrl = process.env.REACT_APP_API_PROD_MANAGER;

          if (isProdManager || isAdmin) {
            promises.push(
              axiosInstance
                .get(`${prodUrl}/product-order-history/pending`)
                .then((res) => ({
                  type: "products",
                  data: res.data?.data || [],
                }))
                .catch(() => ({ type: "products", data: [] })),
            );
          }

          promises.push(
            axiosInstance
              .get(`${prodUrl}/operation-stage-histories/all`)
              .then((res) => ({
                type: "operations",
                data: res.data?.data || [],
              }))
              .catch(() => ({ type: "operations", data: [] })),
          );
        }

        if (promises.length === 0) {
          setLoading(false);
          return;
        }

        const results = await Promise.all(promises);

        const newData = {
          leads: [],
          clients: [],
          enquiries: [],
          operations: [],
          products: [],
          enquiryStages: [],
        };
        results.forEach((result) => {
          if (result && result.type) {
            newData[result.type] = result.data;
          }
        });

        setData(newData);
      } catch (error) {
        console.error("Dashboard error:", error);
        setError("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [
    user,
    viewMode,
    isSales,
    isSalesManager,
    isProdManager,
    isProduction,
    isAdmin,
    showCRM,
    showERP,
  ]);

  const metrics = useMemo(() => {
    const result = {};

    if (data.leads.length > 0) {
      result.totalLeads = data.leads.length;
      result.availableLeads = data.leads.filter(
        (l) => l.leadStatus === "AVAILABLE",
      ).length;
      result.offlineLeads = data.leads.filter(
        (l) => l.leadStatus === "OFFLINE",
      ).length;
    }

    if (data.clients.length > 0) {
      result.totalClients = data.clients.length;
      result.activeClients = data.clients.filter(
        (c) => c.clientStatus === "AVAILABLE",
      ).length;
      result.offlineClients = data.clients.filter(
        (c) => c.clientStatus === "OFFLINE",
      ).length;
    }

    if (data.enquiries.length > 0) {
      const allEnquiries = [];
      data.enquiries.forEach((stage) => {
        (stage.histories || []).forEach((h) => {
          allEnquiries.push({
            ...h,
            stageName: stage.stageName,
            stageType: stage.stageType,
            enquiryStageId: stage.enquiryStageId,
          });
        });
      });

      result.totalEnquiries = allEnquiries.length;
      result.pendingEnquiries = allEnquiries.filter(
        (e) => e.enquiryStatus === "PENDING",
      ).length;
      result.approvedEnquiries = allEnquiries.filter(
        (e) => e.enquiryStatus === "APPROVED",
      ).length;
      result.rejectedEnquiries = allEnquiries.filter(
        (e) => e.enquiryStatus === "REJECTED",
      ).length;

      result.enquiryStages = {};
      allEnquiries.forEach((e) => {
        const stageId = e.enquiryStageId || e.stageName;
        result.enquiryStages[stageId] = {
          name: e.stageName || "Unknown",
          count: (result.enquiryStages[stageId]?.count || 0) + 1,
        };
      });
    }

    // Updated operations processing for the new endpoint structure
    if (data.operations.length > 0) {
      let allOps = [];

      // The new endpoint returns an array of operation categories with nested operationStageHistories
      data.operations.forEach((category) => {
        if (
          category.operationStageHistories &&
          category.operationStageHistories.length > 0
        ) {
          category.operationStageHistories.forEach((history) => {
            allOps.push({
              ...history,
              operationCategoryName: category.operationCatogeryName, // Note: typo in API response
              operationStageName: category.operationStageName,
              operationCategoryId: category.operationCategoryId,
              operationStageId: category.operationStageId,
            });
          });
        }
      });

      result.totalOperations = allOps.length;
      result.pendingOps = allOps.filter(
        (o) => o.operationStatus === "PENDING",
      ).length;
      result.inProgressOps = allOps.filter(
        (o) => o.operationStatus === "IN_PROGRESS",
      ).length;
      result.completedOps = allOps.filter(
        (o) => o.operationStatus === "COMPLETED",
      ).length;
      result.cancelledOps = allOps.filter(
        (o) => o.operationStatus === "CANCELLED",
      ).length;

      // Calculate task completion
      const totalTasks = allOps.reduce(
        (sum, o) => sum + (o.operationStageCheckListHistories || []).length,
        0,
      );
      const completedTasks = allOps.reduce(
        (sum, o) =>
          sum +
          (o.operationStageCheckListHistories || []).filter((t) => t.completed)
            .length,
        0,
      );
      result.taskCompletion =
        totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

      // Store all operations for later use
      result.allOperations = allOps;
    }

    if (data.products.length > 0) {
      result.totalProducts = data.products.length;
      result.pendingProducts = data.products.filter(
        (p) => p.orderStatus === "PENDING",
      ).length;
      result.approvedProducts = data.products.filter(
        (p) => p.orderStatus === "APPROVED",
      ).length;
    }

    return result;
  }, [data]);

  const funnelData = useMemo(() => {
    if (!data.enquiryStages || data.enquiryStages.length === 0) return [];

    const stageCounts = metrics.enquiryStages || {};

    // Separate stages into "normal" and "won" groups, filter out "Lost"
    const normalStages = data.enquiryStages.filter(
      (stage) => stage.stageType !== "Won" && stage.stageType !== "Lost",
    );
    const wonStages = data.enquiryStages.filter(
      (stage) => stage.stageType === "Won",
    );

    // Sort each group by sequence number
    normalStages.sort(
      (a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0),
    );
    wonStages.sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0));

    // Combine: normal stages first, then won stages
    const filteredStages = [...normalStages, ...wonStages];

    // Calculate total count
    const total = filteredStages.reduce(
      (sum, stage) => sum + (stageCounts[stage.enquiryStageId]?.count || 0),
      0,
    );

    return filteredStages.map((stage, index) => {
      const count = stageCounts[stage.enquiryStageId]?.count || 0;
      const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;

      const color =
        stage.stageType === "Won" ? "#10b981" : COLORS[index % COLORS.length];

      return {
        stageName: stage.stageName,
        label: stage.stageName,
        count: count,
        percentage: percentage,
        color: color,
        sequenceNumber: stage.sequenceNumber,
        stageType: stage.stageType,
      };
    });
  }, [data.enquiryStages, metrics.enquiryStages]);

  const lostOrderData = useMemo(() => {
    if (!data.enquiryStages || data.enquiryStages.length === 0) return null;

    const stageCounts = metrics.enquiryStages || {};
    const lostStage = data.enquiryStages.find(
      (stage) => stage.stageType === "Lost",
    );

    if (!lostStage) return null;

    return {
      count: stageCounts[lostStage.enquiryStageId]?.count || 0,
      stageName: lostStage.stageName,
    };
  }, [data.enquiryStages, metrics.enquiryStages]);

  const stageChartData = useMemo(() => {
    if (!metrics.enquiryStages) return [];
    return Object.entries(metrics.enquiryStages).map(([id, info]) => ({
      name: info.name,
      value: info.count,
    }));
  }, [metrics]);

  const statusChartData = useMemo(() => {
    const chartData = [];
    if (metrics.pendingEnquiries)
      chartData.push({
        name: "Pending",
        value: metrics.pendingEnquiries,
        color: COLORS[3],
      });
    if (metrics.approvedEnquiries)
      chartData.push({
        name: "Approved",
        value: metrics.approvedEnquiries,
        color: COLORS[4],
      });
    if (metrics.rejectedEnquiries)
      chartData.push({
        name: "Rejected",
        value: metrics.rejectedEnquiries,
        color: COLORS[2],
      });
    return chartData;
  }, [metrics]);

  const clientStatusData = useMemo(() => {
    const chartData = [];
    if (metrics.activeClients)
      chartData.push({ name: "Active", value: metrics.activeClients });
    if (metrics.offlineClients)
      chartData.push({ name: "Offline", value: metrics.offlineClients });
    return chartData;
  }, [metrics]);

  // ERP specific chart data
  const operationStatusData = useMemo(() => {
    const chartData = [];
    if (metrics.pendingOps)
      chartData.push({
        name: "Pending",
        value: metrics.pendingOps,
        color: COLORS[3],
      });
    if (metrics.inProgressOps)
      chartData.push({
        name: "In Progress",
        value: metrics.inProgressOps,
        color: COLORS[0],
      });
    if (metrics.completedOps)
      chartData.push({
        name: "Completed",
        value: metrics.completedOps,
        color: COLORS[4],
      });
    if (metrics.cancelledOps)
      chartData.push({
        name: "Cancelled",
        value: metrics.cancelledOps,
        color: COLORS[2],
      });
    return chartData;
  }, [metrics]);

  const productStatusData = useMemo(() => {
    const chartData = [];
    if (metrics.pendingProducts)
      chartData.push({ name: "Pending", value: metrics.pendingProducts });
    if (metrics.approvedProducts)
      chartData.push({ name: "Approved", value: metrics.approvedProducts });
    return chartData;
  }, [metrics]);

  const operationStageData = useMemo(() => {
    if (!data.operations || data.operations.length === 0) return [];

    const stageCounts = {};

    // Process the new data structure
    data.operations.forEach((category) => {
      const count = category.operationStageHistories?.length || 0;
      if (count > 0) {
        const stageName =
          category.operationStageName || `Stage ${category.operationStageId}`;
        stageCounts[stageName] = (stageCounts[stageName] || 0) + count;
      }
    });

    return Object.entries(stageCounts)
      .map(([name, count]) => ({
        name,
        value: count,
      }))
      .sort((a, b) => b.value - a.value); // Sort by count descending
  }, [data.operations]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "#f9fafb",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <div style={{ fontSize: "18px", color: "#ef4444" }}>{error}</div>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "0.5rem 1rem",
            background: "#0ea5e9",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "#f9fafb",
        }}
      >
        <div style={{ fontSize: "18px", color: "#6b7280" }}>
          Please log in to view dashboard
        </div>
      </div>
    );
  }

  if (!showCRM && !showERP) {
    return (
      <div className="dashboard-container">
        <div style={{ textAlign: "center", padding: "3rem" }}>
          <h2
            style={{
              fontSize: "1.5rem",
              color: "#111827",
              marginBottom: "1rem",
            }}
          >
            Welcome, {user.employeeName || "User"}!
          </h2>
          <p style={{ color: "#6b7280" }}>
            Your dashboard will be available based on your role assignments.
          </p>
          <div
            style={{
              marginTop: "1rem",
              fontSize: "0.875rem",
              color: "#9ca3af",
            }}
          >
            Current roles: {authorities.join(", ")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Toggle Button - Only show if user has access to both CRM and ERP */}
      {showCRM && showERP && (
        <div className="toggle-buttons">
          <button
            className={viewMode === "CRM" ? "active" : ""}
            onClick={() => handleViewModeToggle("CRM")}
          >
            CRM
          </button>
          <button
            className={viewMode === "ERP" ? "active" : ""}
            disabled={!isProdManager}
            onClick={() => handleViewModeToggle("ERP")}
          >
            ERP
          </button>
        </div>
      )}

      {/* CRM View */}
      {viewMode === "CRM" && showCRM && (
        <>
          <div className="metrics-grid">
            <MetricCard
              title="Total Leads"
              value={metrics.totalLeads || 0}
              color="#0ea5e9"
            />
            <MetricCard
              title="Available Leads"
              value={metrics.availableLeads || 0}
              color="#10b981"
            />
            <MetricCard
              title="Total Clients"
              value={metrics.totalClients || 0}
              color="#7c3aed"
            />
            <MetricCard
              title="Active Clients"
              value={metrics.activeClients || 0}
              color="#10b981"
            />
            <MetricCard
              title="Total Enquiries"
              value={metrics.totalEnquiries || 0}
              color="#f59e0b"
            />
            <MetricCard
              title="Pending"
              value={metrics.pendingEnquiries || 0}
              color="#f59e0b"
            />
            <MetricCard
              title="Approved"
              value={metrics.approvedEnquiries || 0}
              color="#10b981"
            />
            <MetricCard
              title="Rejected"
              value={metrics.rejectedEnquiries || 0}
              color="#ef4444"
            />
          </div>

          <div className="funnel-summary-grid">
            {funnelData.length > 0 && (
              <ChartCard title="Sales Funnel - Enquiry Pipeline" height="280px">
                <ConicalFunnel data={funnelData} />
              </ChartCard>
            )}
            <SummaryStatsCard metrics={metrics} />
          </div>

          <div className="charts-grid">
            {statusChartData.length > 0 && (
              <ChartCard title="Enquiry Status Breakdown">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusChartData}
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      dataKey="value"
                      label={renderPieLabel}
                      labelLine={true}
                    >
                      {statusChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {clientStatusData.length > 0 && (
              <ChartCard title="Client Status Distribution">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={clientStatusData}>
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }}>
                      <Label
                        value="Count"
                        angle={-90}
                        position="insideLeft"
                        style={{ fontSize: "12px", fill: "#6b7280" }}
                      />
                    </YAxis>
                    <Tooltip />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      <LabelList dataKey="value" position="top" />
                      {clientStatusData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {lostOrderData && lostOrderData.count > 0 && (
              <ChartCard title="Lost Orders">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <div
                    style={{
                      fontSize: "3rem",
                      fontWeight: "800",
                      color: "#ef4444",
                    }}
                  >
                    {lostOrderData.count}
                  </div>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: "#6b7280",
                      marginTop: "0.5rem",
                    }}
                  >
                    {lostOrderData.stageName}
                  </div>
                </div>
              </ChartCard>
            )}

            {stageChartData.length > 0 && (
              <ChartCard title="Enquiries by Stage" height="200px">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stageChartData}>
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }}>
                      <Label
                        value="Enquiries"
                        angle={-90}
                        position="insideLeft"
                        style={{ fontSize: "12px", fill: "#6b7280" }}
                      />
                    </YAxis>
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#7c3aed"
                      strokeWidth={2}
                      dot={{ fill: "#7c3aed", r: 4 }}
                      name="Enquiries"
                    >
                      <LabelList dataKey="value" position="top" />
                    </Line>
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>
        </>
      )}

      {/* ERP View */}
      {viewMode === "ERP" && showERP && (
        <>
          <div className="metrics-grid">
            <MetricCard
              title="Total Operations"
              value={metrics.totalOperations || 0}
              color="#0ea5e9"
            />
            <MetricCard
              title="In Progress"
              value={metrics.inProgressOps || 0}
              color="#0ea5e9"
            />
            <MetricCard
              title="Completed"
              value={metrics.completedOps || 0}
              color="#10b981"
            />
            <MetricCard
              title="Pending Ops"
              value={metrics.pendingOps || 0}
              color="#f59e0b"
            />
            <MetricCard
              title="Task Progress"
              value={`${metrics.taskCompletion || 0}%`}
              color="#7c3aed"
            />
            <MetricCard
              title="Product Orders"
              value={metrics.totalProducts || 0}
              color="#ef4444"
            />
          </div>

          <div className="charts-grid">
            {operationStatusData.length > 0 && (
              <ChartCard title="Operation Status Distribution">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={operationStatusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      dataKey="value"
                      label={renderPieLabel}
                      labelLine={true}
                    >
                      {operationStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {productStatusData.length > 0 && (
              <ChartCard title="Product Order Status">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={productStatusData}>
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }}>
                      <Label
                        value="Count"
                        angle={-90}
                        position="insideLeft"
                        style={{ fontSize: "12px", fill: "#6b7280" }}
                      />
                    </YAxis>
                    <Tooltip />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      <LabelList dataKey="value" position="top" />
                      {productStatusData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {operationStageData.length > 0 && (
              <ChartCard title="Operations by Stage" height="200px">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={operationStageData}>
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }}>
                      <Label
                        value="Operations"
                        angle={-90}
                        position="insideLeft"
                        style={{ fontSize: "12px", fill: "#6b7280" }}
                      />
                    </YAxis>
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#0ea5e9"
                      strokeWidth={2}
                      dot={{ fill: "#0ea5e9", r: 4 }}
                      name="Operations"
                    >
                      <LabelList dataKey="value" position="top" />
                    </Line>
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {metrics.taskCompletion !== undefined && (
              <ChartCard title="Task Completion Progress">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: "1rem",
                  }}
                >
                  <div
                    style={{
                      position: "relative",
                      width: "120px",
                      height: "120px",
                    }}
                  >
                    <svg width="120" height="120" viewBox="0 0 120 120">
                      <circle
                        cx="60"
                        cy="60"
                        r="50"
                        fill="none"
                        stroke="#e5e7eb"
                        strokeWidth="10"
                      />
                      <circle
                        cx="60"
                        cy="60"
                        r="50"
                        fill="none"
                        stroke="#7c3aed"
                        strokeWidth="10"
                        strokeDasharray={`${
                          (metrics.taskCompletion / 100) * 314
                        } 314`}
                        strokeLinecap="round"
                        transform="rotate(-90 60 60)"
                      />
                      <text
                        x="60"
                        y="60"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize="24"
                        fontWeight="bold"
                        fill="#7c3aed"
                      >
                        {metrics.taskCompletion}%
                      </text>
                    </svg>
                  </div>
                  <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                    Overall Task Progress
                  </div>
                </div>
              </ChartCard>
            )}
          </div>

          {/* ERP Recent Activity */}
          {data.operations.length > 0 && (
            <div className="activity-section">
              <h3 className="section-title">Recent Operations</h3>
              <div className="activity-list">
                {data.operations
                  .slice(0, 4) // Show more categories
                  .filter(
                    (category) => category.operationStageHistories?.length > 0,
                  )
                  .flatMap((category, idx) =>
                    category.operationStageHistories
                      .slice(0, 2) // Show first 2 from each category
                      .map((history, hIdx) => (
                        <div
                          key={`${category.operationStageId}-${idx}-${hIdx}`}
                          className="activity-item"
                        >
                          <div
                            className="activity-dot"
                            style={{ background: COLORS[idx % COLORS.length] }}
                          />
                          <div className="activity-content">
                            <div className="activity-title">
                              {history.productName ||
                                history.productCode ||
                                "Product Operation"}
                            </div>
                            <div className="activity-meta">
                              {category.operationStageName} •{" "}
                              {history.purchaseOrderCode || "No PO"}
                            </div>
                          </div>
                          <div className="activity-time">
                            {history.dueAt
                              ? new Date(history.dueAt).toLocaleDateString(
                                  "en-IN",
                                  {
                                    month: "short",
                                    day: "numeric",
                                  },
                                )
                              : "No due date"}
                          </div>
                        </div>
                      )),
                  )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Recent Activity for CRM */}
      {viewMode === "CRM" && data.enquiries.length > 0 && (
        <div className="activity-section">
          <h3 className="section-title">Recent Activity</h3>
          <div className="activity-list">
            {data.enquiries.slice(0, 3).map((stage, idx) =>
              stage.histories?.slice(0, 2).map((history, hIdx) => (
                <div key={`${idx}-${hIdx}`} className="activity-item">
                  <div
                    className="activity-dot"
                    style={{ background: COLORS[idx % COLORS.length] }}
                  />
                  <div className="activity-content">
                    <div className="activity-title">
                      {history.leadName || history.clientName || "Unknown"}
                    </div>
                    <div className="activity-meta">{stage.stageName}</div>
                  </div>
                  <div className="activity-time">
                    {history.startedAt
                      ? new Date(history.startedAt).toLocaleDateString(
                          "en-IN",
                          { month: "short", day: "numeric" },
                        )
                      : "N/A"}
                  </div>
                </div>
              )),
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
